// frontend/src/features/inventory/pages/PrintLabelPage.tsx
// ADR-099 Rule LBL-05a/05b. مسیر پیشنهادی: /inventory/lots/:lotId/print?kind=compact|full
// وابستگی جدید: npm install qrcode (+ @types/qrcode برای TS)

import { useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import {
  useV1InventoryLotsRetrieve,
  useV1InventoryLabelPrintSettingsList,
} from "@/api/generated/v1/v1";

export function PrintLabelPage() {
  const { lotId } = useParams<{ lotId: string }>();
  const [searchParams] = useSearchParams();
  const kind = (searchParams.get("kind") as "compact" | "full") || "compact";
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const queryResult = useV1InventoryLotsRetrieve(lotId!, { query: { enabled: !!lotId } });
  const { data: lot, error, isError, isLoading } = queryResult;
  
  console.log("=== PrintLabelPage DEBUG ===");
  console.log("lotId from params:", lotId);
  console.log("queryResult:", queryResult);
  console.log("============================");

  const { data: settingsList } = useV1InventoryLabelPrintSettingsList(undefined, { query: {} });

  const settings = (settingsList?.results ?? []).find((s: any) => s.template_kind === kind);
  const widthMm = settings ? Number(settings.paper_width_mm) : kind === "compact" ? 50 : 100;
  const heightMm = settings ? Number(settings.paper_height_mm) : kind === "compact" ? 30 : 150;

  useEffect(() => {
    if (!lot?.lot_number || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, lot.lot_number, { width: kind === "full" ? 120 : 75, margin: 1 });
  }, [lot?.lot_number, kind]);

  useEffect(() => {
    if (lot && !isError) {
      // آماده که خودکار پرینت باز شود؛ کاربر می‌تواند لغو کند و دستی از دکمهٔ مرورگر چاپ بزند.
      const timer = setTimeout(() => window.print(), 400);
      return () => clearTimeout(timer);
    }
  }, [lot, isError]);

  if (isLoading) {
    return <div style={{ padding: "2rem" }}>در حال بارگذاری واقعی...</div>;
  }

  if (isError || error) {
    const errObj: any = error;
    return (
      <div style={{ padding: "2rem", color: "red" }} dir="ltr">
        <h3>Error loading lot:</h3>
        <pre>{JSON.stringify({ 
          status: errObj?.response?.status, 
          data: errObj?.response?.data,
          message: errObj?.message,
          error: errObj
        }, null, 2)}</pre>
      </div>
    );
  }

  if (!lot) {
    return <div style={{ padding: "2rem" }}>No lot data (not loading, not error, just empty)</div>;
  }

  return (
    <>
      <style>{`
        @media print {
          @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
          html, body, #root, main, .container {
            margin: 0 !important;
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
          }
          .no-print { display: none !important; }
          /* Hide React Query Devtools */
          [class*="tsqd-"], [class*="ReactQueryDevtools"] { display: none !important; }
          .label-box { border: none !important; margin: 0; }
        }
        .label-box {
          width: ${widthMm}mm;
          height: ${heightMm}mm;
          border: 1px dashed #ccc;
          padding: 2mm;
          box-sizing: border-box;
          font-family: Tahoma, sans-serif;
          display: flex;
          overflow: hidden;
        }
      `}</style>

      <div className="no-print" style={{ padding: "1rem", textAlign: "center" }}>
        <button className="btn btn-primary" onClick={() => window.print()}>
          چاپ
        </button>
      </div>

      <div className="label-box" dir="rtl" style={{
        flexDirection: kind === "full" ? "row" : "column",
        justifyContent: kind === "full" ? "flex-start" : "center",
        alignItems: "center",
        gap: "2mm",
      }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1mm", width: kind === "full" ? "32mm" : "20mm" }}>
          <canvas ref={canvasRef} />
          <div style={{ fontWeight: "bold", fontSize: kind === "full" ? "3mm" : "2.5mm", textAlign: "center", wordWrap: "break-word", width: "100%" }} dir="ltr">
            {lot.lot_number}
          </div>
        </div>

        {kind === "compact" && (
          <div style={{ fontSize: "2.5mm", textAlign: "center", lineHeight: 1.2 }}>
            {lot.material_code || "-"} — {lot.material_name || "-"}
          </div>
        )}

        {kind === "full" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5mm", textAlign: "right", flex: 1 }}>
            <div style={{ fontSize: "5mm", fontWeight: "bold", lineHeight: 1.2 }}>
              {lot.material_name || "-"}
            </div>
            <div style={{ fontSize: "3mm", color: "#444" }}>
              کد: {lot.material_code || "-"}
            </div>
            <div style={{ fontSize: "3mm" }}>شماره بارنامه: {lot.reference_doc || "—"}</div>
            <div style={{ fontSize: "3mm" }}>
              مقدار: <span dir="ltr">{lot.received_qty} {lot.stock_uom_symbol}</span>
            </div>
            <div style={{ fontSize: "3mm" }}>
              ضریب: <span dir="ltr">{lot.effective_numerator} ({lot.production_uom_symbol}) = {lot.effective_denominator} ({lot.stock_uom_symbol})</span>
            </div>
            <div style={{ fontSize: "3mm" }}>تاریخ ورود: <span dir="ltr">{lot.received_date_jalali}</span></div>
          </div>
        )}
      </div>
    </>
  );
}
